import { KeyboardEvent, ReactNode, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

// Re-exported so the SSO forms keep importing it from here; it is shared with the MCP settings page.
export { CopyableField } from "components/ui/copyable_field/CopyableField";

export const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  maxWidth: 750,
});

export function FieldGroup({ children }: { children: ReactNode }) {
  return (
    <Flex column gap={6}>
      {children}
    </Flex>
  );
}

export const ErrorText = ({ children }: { children: React.ReactNode }) => (
  <Text inline description color="sysDestructiveDefault">
    {children}
  </Text>
);

export const ToggleRow = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
});

const DomainInputWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap" as const,
  gap: 4,
  padding: "4px 8px",
  minHeight: 32,
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundDefault,
  alignItems: "center",
  cursor: "text",
  "&:focus-within": {
    borderColor: theme.colors.sysPrimaryDefault,
    boxShadow: `0 0 0 1px ${theme.colors.sysPrimaryDefault}`,
  },
}));

const DomainTag = styled.span(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 8px",
  borderRadius: 4,
  backgroundColor: theme.colors.sysPrimarySubtle,
  color: theme.colors.sysPrimaryDefault,
  fontSize: 13,
  fontWeight: theme.font.fontWeightMedium,
}));

const TagRemove = styled.button(({ theme }) => ({
  all: "unset",
  cursor: "pointer",
  fontSize: 14,
  lineHeight: 1,
  color: theme.colors.sysPrimaryDefault,
  "&:hover": {
    color: theme.colors.sysTextDefault,
  },
}));

const DomainTextInput = styled.input({
  all: "unset",
  flex: 1,
  minWidth: 80,
  fontSize: 14,
});

type TagInputProps = {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  separators?: string[];
  lowercase?: boolean;
  onBlur?: () => void;
};

export function TagInput({
  values,
  onChange,
  placeholder,
  separators = ["Enter", ","],
  lowercase = true,
  onBlur,
}: TagInputProps) {
  const [input, setInput] = useState("");

  function add(value: string) {
    const trimmed = lowercase ? value.trim().toLowerCase() : value.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInput("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (separators.includes(e.key)) {
      e.preventDefault();
      add(input);
    }
    if (e.key === "Backspace" && input === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  return (
    <DomainInputWrapper>
      {values.map((value) => (
        <DomainTag key={value}>
          {value}
          <TagRemove onClick={() => onChange(values.filter((v) => v !== value))} type="button">
            &times;
          </TagRemove>
        </DomainTag>
      ))}
      <DomainTextInput
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (input.trim()) add(input);
          onBlur?.();
        }}
        placeholder={values.length === 0 ? placeholder : ""}
      />
    </DomainInputWrapper>
  );
}
